import {
	Scene,
	Vector3,
	MeshBuilder,
	StandardMaterial,
	Color3,
	TransformNode,
	Mesh,
	Animation,
	AnimationGroup,
	CubicEase,
	EasingFunction,
	IAnimationKey,
} from "@babylonjs/core";

const VAN_COLORS = [
	new Color3(0.7, 0.18, 0.18),
	new Color3(0.18, 0.45, 0.7),
	new Color3(0.85, 0.7, 0.2),
	new Color3(0.3, 0.55, 0.3),
];

function buildVan(scene: Scene, id: number): TransformNode {
	const root = new TransformNode(`van-${id}`, scene);

	const bodyMat = new StandardMaterial(`van-body-${id}`, scene);
	bodyMat.diffuseColor = VAN_COLORS[id % VAN_COLORS.length]!;
	const wheelMat = new StandardMaterial(`van-wheel-${id}`, scene);
	wheelMat.diffuseColor = new Color3(0.08, 0.08, 0.1);
	const windowMat = new StandardMaterial(`van-window-${id}`, scene);
	windowMat.diffuseColor = new Color3(0.4, 0.55, 0.7);
	windowMat.emissiveColor = new Color3(0.05, 0.1, 0.15);

	const cargo = MeshBuilder.CreateBox(`van-cargo-${id}`, { width: 2.4, depth: 3, height: 2 }, scene);
	cargo.position.set(0, 1.4, -0.5);
	cargo.material = bodyMat;
	cargo.parent = root;

	const cab = MeshBuilder.CreateBox(`van-cab-${id}`, { width: 2.2, depth: 1.6, height: 1.6 }, scene);
	cab.position.set(0, 1.2, 1.6);
	cab.material = bodyMat;
	cab.parent = root;

	const window = MeshBuilder.CreateBox(`van-win-${id}`, { width: 2.0, depth: 0.1, height: 0.7 }, scene);
	window.position.set(0, 1.55, 2.4);
	window.material = windowMat;
	window.parent = root;

	for (const [wx, wz] of [
		[-1.05, 1.6],
		[1.05, 1.6],
		[-1.05, -1.4],
		[1.05, -1.4],
	] as const) {
		const w = MeshBuilder.CreateCylinder(`van-wh-${id}-${wx}-${wz}`, { diameter: 0.7, height: 0.3 }, scene);
		w.rotation.z = Math.PI / 2;
		w.position.set(wx, 0.35, wz);
		w.material = wheelMat;
		w.parent = root;
	}

	return root;
}

interface ActiveVan {
	root: TransformNode;
	anim: AnimationGroup;
}

const active = new Set<ActiveVan>();
let counter = 0;

const ROUTES: Array<{ start: Vector3; end: Vector3 }> = [
	// East-west main road (passing south of warehouse)
	{ start: new Vector3(-50, 0, -4), end: new Vector3(50, 0, -4) },
	{ start: new Vector3(50, 0, -4), end: new Vector3(-50, 0, -4) },
	// North-south east-side road
	{ start: new Vector3(22, 0, -30), end: new Vector3(22, 0, 40) },
	{ start: new Vector3(22, 0, 40), end: new Vector3(22, 0, -30) },
	// North-south west-side road
	{ start: new Vector3(-22, 0, -30), end: new Vector3(-22, 0, 40) },
	{ start: new Vector3(-22, 0, 40), end: new Vector3(-22, 0, -30) },
];

export function dispatchAmbientVan(scene: Scene) {
	const route = ROUTES[Math.floor(Math.random() * ROUTES.length)]!;
	const id = counter++;
	const root = buildVan(scene, id);
	root.position.copyFrom(route.start);
	root.lookAt(route.end);

	const ease = new CubicEase();
	ease.setEasingMode(EasingFunction.EASINGMODE_EASEINOUT);

	const anim = new Animation("van-pos", "position", 60, Animation.ANIMATIONTYPE_VECTOR3, Animation.ANIMATIONLOOPMODE_CONSTANT);
	anim.setKeys([
		{ frame: 0, value: root.position.clone() },
		{ frame: 360, value: route.end.clone() },
	]);
	anim.setEasingFunction(ease);

	const group = new AnimationGroup(`van-amb-${id}`, scene);
	group.addTargetedAnimation(anim, root);
	const handle: ActiveVan = { root, anim: group };
	active.add(handle);

	group.onAnimationGroupEndObservable.add(() => {
		root.dispose();
		active.delete(handle);
	});
	group.play(false);
}

/**
 * Spawn a delivery van that drives in from -X, stops at the warehouse doorway,
 * idles 2s, then drives off down +X. Auto-disposes when done.
 */
export function dispatchDeliveryVan(scene: Scene) {
	const id = counter++;
	const root = buildVan(scene, id);

	const start = new Vector3(-50, 0, -2);
	const dock = new Vector3(0, 0, -2); // just south of warehouse doorway
	const end = new Vector3(50, 0, -2);
	root.position.copyFrom(start);
	root.lookAt(dock);

	const ease = new CubicEase();
	ease.setEasingMode(EasingFunction.EASINGMODE_EASEINOUT);

	const driveTo = (target: Vector3, durationFrames: number) => {
		const anim = new Animation("van-pos", "position", 60, Animation.ANIMATIONTYPE_VECTOR3, Animation.ANIMATIONLOOPMODE_CONSTANT);
		const keys: IAnimationKey[] = [
			{ frame: 0, value: root.position.clone() },
			{ frame: durationFrames, value: target.clone() },
		];
		anim.setKeys(keys);
		anim.setEasingFunction(ease);
		return anim;
	};

	const driveIn = driveTo(dock, 120);
	root.animations = [driveIn];
	const group = new AnimationGroup(`van-anim-${id}`, scene);
	group.addTargetedAnimation(driveIn, root);

	const handle: ActiveVan = { root, anim: group };
	active.add(handle);

	group.onAnimationGroupEndObservable.add(() => {
		// pause briefly to "pick up", then drive off
		setTimeout(() => {
			const driveOut = driveTo(end, 120);
			const g2 = new AnimationGroup(`van-anim-out-${id}`, scene);
			g2.addTargetedAnimation(driveOut, root);
			root.lookAt(end);
			g2.onAnimationGroupEndObservable.add(() => {
				root.dispose();
				active.delete(handle);
			});
			g2.play(false);
		}, 1800);
	});
	group.play(false);
}
